import {
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  OpenInNew as LinkIcon,
  Medication as SupplementIcon,
} from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchSupplements, deleteSupplement, createSupplement } from "../services/SupplementService";
import { useMessageContext } from "../contexts/MessageContext";
import { useCompany } from "../contexts/CompanyContext";
import { useAuth } from "../contexts/AuthContext";
import { ConfirmDialog, Pagination } from "../componenets/ui";
import { t } from "../componenets/dashboard/coach/coachTokens";
import NewSupplementDialog from "../componenets/nutrition/NewSupplementDialog";
import { imageUrl } from "../utils/imageUrl";

const PAGE_SIZE = 8;

/* ── Table cell presets (match Clients / Exercises / Food) ── */
const td = { padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle" };
const th = { padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
             background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em" };

const SupplementsScreen = () => {
  const navigate = useNavigate();
  const { notificationHandler } = useMessageContext();
  const { company } = useCompany();
  const { isSuperAdmin } = useAuth();
  const canCreate = !isSuperAdmin() || !!company?.id;

  const [state, setState] = useState({
    supplements: [],
    total: 0,
    keyword: "",
    page: 0,
    loading: true,
  });

  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: "" });
  const [newDialogOpen, setNewDialogOpen] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const debounceRef = useRef(null);

  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setState(prev => ({ ...prev, keyword: val, page: 0 }));
    }, 300);
  }, []);

  useEffect(() => {
    setState(p => ({ ...p, loading: true }));
    searchSupplements({ page: state.page, size: PAGE_SIZE, keyword: state.keyword, companyId: company?.id })
      .then(r => {
        setState(p => ({
          ...p,
          supplements: r.data?.content || [],
          total: r.data?.totalElements || 0,
          loading: false,
        }));
      })
      .catch(() => setState(p => ({ ...p, loading: false })));
  }, [state.page, state.keyword, company]);

  const handleDelete = async () => {
    const res = await deleteSupplement(deleteDialog.id);
    if (res.success) {
      notificationHandler.success(res.message);
      setState(p => ({ ...p, page: 0, keyword: "" }));
      setSearchInput("");
    } else {
      notificationHandler.error(res.message);
    }
    setDeleteDialog({ open: false, id: null, name: "" });
  };

  const handleCreate = async (supplementData) => {
    const payload = company?.id ? { ...supplementData, companyId: company.id } : supplementData;
    const res = await createSupplement(payload);
    if (res.success) {
      notificationHandler.success(res.message);
      setNewDialogOpen(false);
      setState(p => ({ ...p, page: 0, keyword: "", loading: true }));
      setSearchInput("");
    } else {
      notificationHandler.error(res.message);
    }
  };

  const pages = Math.ceil(state.total / PAGE_SIZE);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null, name: "" })}
        onConfirm={handleDelete}
        title="מחיקת תוסף"
        description={`האם למחוק את התוסף "${deleteDialog.name}"?`}
        variant="danger"
        confirmText="מחק"
      />

      {/* New supplement dialog */}
      <NewSupplementDialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        onSubmit={handleCreate}
      />

      {/* ─── Title row (on canvas) ──────────────────────── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: "22px", md: "26px" }, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px" }}>
            מאגר תוספים
          </Typography>
          <Typography sx={{ fontSize: "13px", color: t.t2, mt: 0.25 }}>ניהול תוספי תזונה</Typography>
        </Box>
        <Box
          component="button"
          onClick={() => canCreate && setNewDialogOpen(true)}
          disabled={!canCreate}
          title={!canCreate ? "בחר חברה כדי ליצור תוסף" : ""}
          sx={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: t.green, color: "#fff",
            height: 34, px: "15px", borderRadius: t.rs,
            fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
            border: "none", cursor: canCreate ? "pointer" : "not-allowed", fontFamily: "inherit",
            transition: "all .14s",
            "&:hover": canCreate ? { background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)" } : {},
            "&:disabled": { background: t.border, color: t.t3, cursor: "not-allowed" },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          תוסף חדש
        </Box>
      </Box>

      {/* ─── Table card ──────────────────────────────────── */}
      <Box sx={{
        background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
        boxShadow: t.sh, overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>

        {/* Filter bar inside card */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: "7px",
          padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
          background: t.bg, flexWrap: "wrap", flexShrink: 0, direction: "rtl",
        }}>
          {/* Count pill */}
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            fontSize: "12px", fontWeight: 700, color: t.greenText,
            background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
            padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
          }}>
            <SupplementIcon sx={{ fontSize: 14 }} />
            {state.total} תוספים
          </Box>

          {/* Search */}
          <Box sx={{ position: "relative" }}>
            <SearchIcon sx={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none" }} />
            <InputBase
              value={searchInput}
              onChange={e => handleSearch(e.target.value)}
              placeholder="חיפוש לפי שם..."
              sx={{
                width: 220,
                padding: "6px 10px 6px 30px",
                border: `1.5px solid ${t.border}`,
                borderRadius: t.rs,
                background: t.white,
                color: t.text,
                fontSize: "12px",
                "& input": { padding: 0 },
                "& input::placeholder": { color: t.t3, opacity: 1 },
                "&:focus-within": { borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)" },
              }}
            />
          </Box>
        </Box>

        {/* Table area */}
        {state.loading && !state.supplements.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540 }}>
            <CircularProgress size={32} sx={{ color: t.green }} />
          </Box>
        ) : !state.supplements.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px" }}>
            <Typography sx={{ color: t.t3, fontSize: "15px", fontWeight: 500 }}>אין תוספים במאגר</Typography>
            <Typography sx={{ color: t.t3, fontSize: "13px" }}>לחץ על "תוסף חדש" כדי להוסיף</Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowY: "auto", maxHeight: 540, direction: "rtl" }}>
              <Table stickyHeader sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...th, textAlign: "center", width: "8%" }}></TableCell>
                    <TableCell sx={{ ...th, textAlign: "right", width: "25%" }}>שם תוסף</TableCell>
                    <TableCell sx={{ ...th, textAlign: "right", width: "35%", display: { xs: "none", md: "table-cell" } }}>תיאור</TableCell>
                    <TableCell sx={{ ...th, textAlign: "center", width: "12%", display: { xs: "none", md: "table-cell" } }}>קישור לרכישה</TableCell>
                    <TableCell sx={{ ...th, textAlign: "center", width: "10%" }}>פעולות</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.supplements.map(supplement => (
                    <TableRow
                      key={supplement.id}
                      onClick={() => navigate(`/app/supplements/${supplement.id}`)}
                      sx={{ cursor: "pointer", "&:hover td": { background: t.greenLight }, transition: "background .12s ease" }}
                    >
                      <TableCell sx={{ ...td, textAlign: "center" }}>
                        <Avatar src={imageUrl(supplement.imageUrl, 36)} sx={{ width: 36, height: 36, mx: "auto", bgcolor: t.greenLight, color: t.green }} variant="rounded">
                          <SupplementIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                      </TableCell>
                      <TableCell sx={{ ...td, textAlign: "right" }}>
                        <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {supplement.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...td, textAlign: "right", display: { xs: "none", md: "table-cell" } }}>
                        <Typography sx={{ fontSize: "12.5px", color: t.t2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {supplement.description || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...td, textAlign: "center", display: { xs: "none", md: "table-cell" } }}>
                        {supplement.purchaseLink ? (
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); window.open(supplement.purchaseLink, "_blank"); }}
                            sx={{ color: t.green, "&:hover": { background: t.greenLight } }}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        ) : <Typography sx={{ fontSize: "12px", color: t.t3 }}>—</Typography>}
                      </TableCell>
                      <TableCell sx={{ ...td, textAlign: "center" }}>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(`/app/supplements/${supplement.id}`); }}
                            sx={{ color: t.green, "&:hover": { background: t.greenLight } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, id: supplement.id, name: supplement.name }); }}
                            sx={{ color: t.red, "&:hover": { background: t.redLight } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {pages > 1 && (
              <Pagination
                currentPage={state.page}
                totalPages={pages}
                totalItems={state.total}
                itemsPerPage={PAGE_SIZE}
                onPageChange={p => setState(prev => ({ ...prev, page: p }))}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default SupplementsScreen;
